import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  documentApi,
  documentRequestApi,
  merchantUploadApi,
  type DocumentCategory,
  type DocumentRequestPayload,
  type DocumentUploadPayload,
  type EmailDocumentPayload,
} from '../lib/api'

export type DocumentAnchor =
  | { kind: 'lead'; id: string }
  | { kind: 'merchant'; id: string }

function documentsQueryKey(anchor: DocumentAnchor | undefined, search?: string) {
  return [anchor?.kind === 'merchant' ? 'merchant-documents' : 'lead-documents', anchor?.id, search ?? '']
}

function documentRequestsQueryKey(anchor: DocumentAnchor | undefined) {
  return [anchor?.kind === 'merchant' ? 'merchant-document-requests' : 'document-requests', anchor?.id]
}

function invalidateDocumentFamily(queryClient: ReturnType<typeof useQueryClient>, anchor: DocumentAnchor) {
  queryClient.invalidateQueries({ queryKey: documentsQueryKey(anchor) })
  queryClient.invalidateQueries({ queryKey: ['lead-documents'] })
  queryClient.invalidateQueries({ queryKey: ['merchant-documents'] })
}

function invalidateDocumentRequestFamily(queryClient: ReturnType<typeof useQueryClient>, anchor: DocumentAnchor) {
  queryClient.invalidateQueries({ queryKey: documentRequestsQueryKey(anchor) })
  queryClient.invalidateQueries({ queryKey: ['document-requests'] })
  queryClient.invalidateQueries({ queryKey: ['merchant-document-requests'] })
}

/**
 * Flat document list for a lead or merchant. Accepts an optional search term that
 * the backend uses for ILIKE substring match on file_name + label.
 * The hook is debounced at the caller site (DocumentCenter) — the
 * hook itself passes the current value straight through so cache keys
 * track the exact query the backend ran.
 */
export function useDocuments(anchor: DocumentAnchor | undefined, search?: string) {
  return useQuery({
    queryKey: documentsQueryKey(anchor, search),
    queryFn: () => {
      if (!anchor) throw new Error('Document anchor is required')
      return anchor.kind === 'merchant'
        ? documentApi.listForMerchant(anchor.id, search?.trim() || undefined)
        : documentApi.listForLead(anchor.id, search?.trim() || undefined)
    },
    enabled: !!anchor?.id,
  })
}

export function useLeadDocuments(leadId: string | undefined, search?: string) {
  return useDocuments(leadId ? { kind: 'lead', id: leadId } : undefined, search)
}

/**
 * Upload a document to a lead or merchant. New shape: free-form label + optional
 * is_restricted flag. The legacy category field is still accepted for
 * any internal caller that hasn't migrated.
 */
export function useUploadDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ anchor, payload }: { anchor: DocumentAnchor; payload: DocumentUploadPayload }) =>
      anchor.kind === 'merchant'
        ? documentApi.uploadForMerchant(anchor.id, payload)
        : documentApi.upload(anchor.id, payload),
    onSuccess: (_data, { anchor }) => {
      invalidateDocumentFamily(queryClient, anchor)
    },
  })
}

/**
 * Soft-delete a single document (super_admin only).
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (documentId: string) => documentApi.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-documents'] })
      queryClient.invalidateQueries({ queryKey: ['merchant-documents'] })
    },
  })
}

/**
 * Bulk soft-delete (super_admin only).
 */
export function useBulkDeleteDocuments() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ anchor, ids }: { anchor: DocumentAnchor; ids: string[] }) =>
      anchor.kind === 'merchant'
        ? documentApi.bulkDeleteForMerchant(anchor.id, ids)
        : documentApi.bulkDelete(anchor.id, ids),
    onSuccess: (_data, { anchor }) => {
      invalidateDocumentFamily(queryClient, anchor)
    },
  })
}

/**
 * Build + download a zip of selected documents. Returns a signed S3
 * URL the caller opens in a new tab (mutation result — no cache
 * invalidation needed since nothing in the document list changed).
 */
export function useBulkDownloadDocuments() {
  return useMutation({
    mutationFn: ({ anchor, ids }: { anchor: DocumentAnchor; ids: string[] }) =>
      anchor.kind === 'merchant'
        ? documentApi.bulkDownloadForMerchant(anchor.id, ids)
        : documentApi.bulkDownload(anchor.id, ids),
  })
}

/**
 * Fetch a fresh 30-min signed preview URL for a single document. The
 * caller uses the response to open the file in a new tab — the URL on
 * the row list may have aged past its TTL by the time ops clicks.
 */
export function useDocumentPreview() {
  return useMutation({
    mutationFn: (documentId: string) => documentApi.preview(documentId),
  })
}

/**
 * Queue an email delivery of a document to an external recipient.
 * Returns 202 — the job dispatches asynchronously on the default queue.
 */
export function useEmailDocument() {
  return useMutation({
    mutationFn: ({ documentId, payload }: { documentId: string; payload: EmailDocumentPayload }) =>
      documentApi.email(documentId, payload),
  })
}

/**
 * List available PDF-from-Lead templates. Cached per lead because the
 * list can vary with lead state (future: stage-specific templates).
 */
export function usePdfTemplates(leadId: string | undefined) {
  return useQuery({
    queryKey: ['pdf-templates', leadId],
    queryFn: () => documentApi.pdfTemplates(leadId!),
    enabled: !!leadId,
  })
}

/**
 * Generate a pre-filled document from lead data. On success the
 * Document Center list is invalidated so the new row appears.
 */
export function useGeneratePdf() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leadId, template_key, label }: { leadId: string; template_key: string; label?: string }) =>
      documentApi.generatePdf(leadId, template_key, label),
    onSuccess: (_data, { leadId }) => {
      queryClient.invalidateQueries({ queryKey: ['lead-documents', leadId] })
    },
  })
}

/**
 * Create a merchant document request (sends email with upload link).
 */
export function useRequestDocuments() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ anchor, data }: { anchor: DocumentAnchor; data: DocumentRequestPayload }) =>
      anchor.kind === 'merchant'
        ? documentApi.requestDocumentsForMerchant(anchor.id, data)
        : documentApi.requestDocuments(anchor.id, data),
    onSuccess: (_data, { anchor }) => {
      invalidateDocumentRequestFamily(queryClient, anchor)
    },
  })
}

/**
 * Fetch document requests for a lead or merchant.
 */
export function useDocumentRequests(anchor: DocumentAnchor | undefined) {
  return useQuery({
    queryKey: documentRequestsQueryKey(anchor),
    queryFn: () => {
      if (!anchor) throw new Error('Document request anchor is required')
      return anchor.kind === 'merchant'
        ? documentRequestApi.listForMerchant(anchor.id)
        : documentRequestApi.listForLead(anchor.id)
    },
    enabled: !!anchor?.id,
  })
}

/**
 * Void (archive) a pending document request.
 * Caller supplies the anchor so we can invalidate the correct cache entry.
 */
export function useVoidDocumentRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentRequestId }: { documentRequestId: string; anchor: DocumentAnchor }) =>
      documentRequestApi.void(documentRequestId),
    onSuccess: (_data, { anchor }) => {
      invalidateDocumentRequestFamily(queryClient, anchor)
    },
  })
}

/**
 * Fetch merchant upload page info (public, no auth).
 */
export function useMerchantUploadInfo(token: string | undefined) {
  return useQuery({
    queryKey: ['merchant-upload', token],
    queryFn: () => merchantUploadApi.getUploadInfo(token!),
    enabled: !!token,
    retry: false,
  })
}

/**
 * Merchant file upload (public, no auth).
 */
export function useMerchantUpload() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ token, category, file }: { token: string; category: DocumentCategory; file: File }) =>
      merchantUploadApi.upload(token, category, file),
    onSuccess: (_data, { token }) => {
      queryClient.invalidateQueries({ queryKey: ['merchant-upload', token] })
    },
  })
}
